import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Lock, LockOpen, UserRound, UserPlus, FileText, Upload, Trash2, Download,
  Wallet, Camera, Phone, Mail, CalendarDays, CheckCircle2, LayoutDashboard, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";
const inputCls =
  "w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "No date";

export default function Rota({ embedded = false }) {
  const [gate, setGate] = useState("loading");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [gateErr, setGateErr] = useState("");
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const GRADES = [
    { value: "first_aider", label: "First Aider / Medical" },
    { value: "level_3", label: "Level 3" },
    { value: "level_4", label: "Level 4" },
    { value: "technician", label: "Technician (EMT)" },
    { value: "paramedic", label: "Paramedic" },
    { value: "doctor", label: "Doctor" },
  ];
  const [profile, setProfile] = useState({ name: "", email: "", phone: "", role_label: "", grade: "", notes: "" });
  const [payDates, setPayDates] = useState({});
  const [partAmounts, setPartAmounts] = useState({});

  const partPay = async (entry) => {
    const amt = parseFloat(partAmounts[entry.entry_id]);
    if (!amt || amt <= 0) { toast.error("Enter the part-payment amount first"); return; }
    try {
      const { data } = await api.patch(`/events/${entry.event_id}/roster/${entry.entry_id}/payment`,
        { paid: false, part_amount: amt, paid_date: payDates[entry.entry_id] || "" });
      toast.success(data.message);
      setPartAmounts((p) => ({ ...p, [entry.entry_id]: "" }));
      await loadDirectory(true);
      const { data: d } = await api.get(`/staff-directory/${selected.key}`);
      setSelected(d); setProfile({ name: d.name, phone: d.phone, role_label: d.role_label, grade: d.grade || "first_aider", notes: d.notes || "" });
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const [feedUrl, setFeedUrl] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", grade: "first_aider" });
  const [saving, setSaving] = useState(false);
  const [availCursor, setAvailCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const availMonth = {
    y: availCursor.getFullYear(),
    m: availCursor.getMonth(),
    offset: (new Date(availCursor.getFullYear(), availCursor.getMonth(), 1).getDay() + 6) % 7,
    count: new Date(availCursor.getFullYear(), availCursor.getMonth() + 1, 0).getDate(),
    today: isoDay(new Date()),
  };
  const photoRef = useRef(null);
  const docRef = useRef(null);

  const loadDirectory = useCallback(async (silent = false) => {
    try {
      const { data } = await api.get("/staff-directory");
      setStaff(data);
      setGate("open");
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setGate((g) => (g === "loading" ? "locked" : g));
        if (!silent) setGate((g) => (g === "open" ? g : "locked"));
      } else if (!silent) {
        toast.error(formatApiError(e));
      }
    }
  }, []);

  const addPerson = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/staff-directory", addForm);
      toast.success(`${addForm.name} added to the staff list`);
      setAddForm({ name: "", email: "", phone: "", grade: "first_aider" });
      setAddOpen(false);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const acceptStaff = async (s) => {
    if (!window.confirm(`Accept ${s.name}? This creates their staff login and emails them a temporary password.`)) return;
    try {
      const { data } = await api.post(`/staff-directory/${encodeURIComponent(s.key)}/accept`);
      toast.success(data.message);
      if (data.temp_password) window.alert(`Email could not be sent — give them this temporary password: ${data.temp_password}`);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the staff pool? Their name stays on past bookings and records.`)) return;
    try {
      const { data } = await api.delete(`/staff-directory/${encodeURIComponent(s.key)}`);
      toast.success(data.message);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-portal/status");
        if (!data.pin_set) {
          setGate("setup");
          return;
        }
      } catch (e) {
        /* fall through and try the directory */
      }
      loadDirectory(true);
    })();
  }, [loadDirectory]);

  const setupPin = async (e) => {
    e.preventDefault();
    setGateErr("");
    if (pin.length < 4) return setGateErr("PIN must be at least 4 characters");
    if (pin !== pin2) return setGateErr("PINs do not match");
    setSaving(true);
    try {
      await api.post("/staff-portal/set-pin", { pin });
      await api.post("/staff-portal/unlock", { pin });
      toast.success("Staff portal PIN set — keep it safe");
      setPin(""); setPin2("");
      loadDirectory(true);
    } catch (err) {
      setGateErr(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setGateErr("");
    setSaving(true);
    try {
      await api.post("/staff-portal/unlock", { pin });
      setPin("");
      loadDirectory(true);
    } catch (err) {
      setGateErr(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const lock = async () => {
    try {
      await api.post("/staff-portal/lock");
    } catch (e) { console.debug("Staff portal lock request failed:", e?.message || e); }
    setSelected(null);
    setGate("locked");
  };

  const openStaff = async (key) => {
    try {
      const { data } = await api.get(`/staff-directory/${encodeURIComponent(key)}`);
      setSelected(data);
      setProfile({ name: data.name, email: data.email, phone: data.phone, role_label: data.role_label, grade: data.grade || "", notes: data.notes });
      setPayDates({});
      if (data.has_photo) {
        const img = await api.get(`/staff-directory/${encodeURIComponent(key)}/photo-image`, { responseType: "blob" });
        setPhotoUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(img.data);
        });
      } else {
        setPhotoUrl("");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const emailShifts = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/staff-directory/${encodeURIComponent(selected.key)}/email-shifts`);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const getFeedLink = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/staff-directory/${encodeURIComponent(selected.key)}/feed-link`);
      setFeedUrl(`${process.env.REACT_APP_BACKEND_URL}${data.path}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put(`/staff-directory/${encodeURIComponent(selected.key)}/profile`, profile);
      toast.success("Profile saved");
      openStaff(selected.key);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/staff-directory/${encodeURIComponent(selected.key)}/${kind}`, fd);
      toast.success(kind === "photo" ? "Photo uploaded" : "Document uploaded");
      openStaff(selected.key);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(
        `/staff-directory/${encodeURIComponent(selected.key)}/documents/${doc.id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await api.delete(`/staff-directory/${encodeURIComponent(selected.key)}/documents/${doc.id}`);
      toast.success("Document deleted");
      openStaff(selected.key);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePaid = async (entry) => {
    try {
      await api.patch(`/events/${entry.event_id}/roster/${entry.entry_id}/payment`, {
        paid: !entry.paid,
        paid_date: payDates[entry.entry_id] || "",
      });
      toast.success(!entry.paid ? "Marked as paid" : "Marked as unpaid");
      openStaff(selected.key);
      loadDirectory(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePaidDate = async (entry) => {
    const d = payDates[entry.entry_id];
    if (!d) return;
    try {
      await api.patch(`/events/${entry.event_id}/roster/${entry.entry_id}/payment`, { paid: entry.paid, paid_date: d });
      toast.success("Payment date saved");
      openStaff(selected.key);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (gate === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F4F7FA]">
        <div className="h-10 w-10 rounded-full border-4 border-[#005EB8] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (gate === "setup" || gate === "locked") {
    const isSetup = gate === "setup";
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center p-6" data-testid="rota-gate">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-xl">
          <div className="bg-white rounded-lg p-2 inline-block mb-4">
            <img src={LOGO} alt="Wales Event Medical Services" className="h-10 w-auto" />
          </div>
          <h1 className="font-heading font-extrabold text-xl text-[#0F2547] mb-1 flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#005EB8]" /> {isSetup ? "Set a staff portal PIN" : "Staff portal locked"}
          </h1>
          <p className="text-sm text-slate-500 mb-5">
            {isSetup
              ? "This area holds wages, documents and staff records. Set a separate PIN to protect it."
              : "Enter your staff portal PIN to continue."}
          </p>
          <form onSubmit={isSetup ? setupPin : unlock} className="space-y-3">
            <input
              type="password" inputMode="numeric" autoComplete="off" required
              placeholder={isSetup ? "Choose a PIN (min 4 characters)" : "Staff portal PIN"}
              value={pin} onChange={(e) => setPin(e.target.value)}
              data-testid="rota-pin-input" className={inputCls}
            />
            {isSetup && (
              <input
                type="password" inputMode="numeric" autoComplete="off" required
                placeholder="Confirm PIN" value={pin2} onChange={(e) => setPin2(e.target.value)}
                data-testid="rota-pin-confirm-input" className={inputCls}
              />
            )}
            {gateErr && <p className="text-xs font-semibold text-[#D5281B]" data-testid="rota-gate-error">{gateErr}</p>}
            <button type="submit" disabled={saving} data-testid="rota-pin-submit"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm disabled:opacity-60">
              {saving ? "Please wait…" : isSetup ? "Set PIN & open" : "Unlock"}
            </button>
          </form>
          <Link to="/dashboard" data-testid="rota-gate-back" className="block text-center text-xs font-semibold text-slate-400 hover:text-[#005EB8] mt-4 min-h-[36px] leading-[36px]">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="rota-page">
      {!embedded && (
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 py-3 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="WEMS" className="h-9 w-auto" />
          <div className="hidden sm:block">
            <p className="font-heading font-bold text-[#0F2547] text-sm leading-tight">Rota & Staff Portal</p>
            <p className="text-[11px] text-slate-500">Admin only · Wages, documents & records</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/dashboard" data-testid="rota-back-dashboard"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]">
            <LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link to="/calendar" data-testid="rota-back-calendar"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]">
            <CalendarDays className="h-4 w-4" /> <span className="hidden sm:inline">Calendar</span>
          </Link>
          <button onClick={lock} data-testid="rota-lock-btn"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors min-h-[44px]">
            <LockOpen className="h-4 w-4" /> Lock
          </button>
        </div>
      </header>
      )}

      <main className="max-w-6xl mx-auto p-4 sm:p-8">
        {!selected ? (
          <>
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h1 className="font-heading text-2xl font-extrabold text-[#0F2547] mb-1">Staff</h1>
                <p className="text-sm text-slate-500">Everyone on your team — booked onto events or not. Tap a person for their full record.</p>
              </div>
              <button onClick={() => setAddOpen((v) => !v)} data-testid="rota-add-staff-btn"
                className="inline-flex items-center gap-2 text-xs font-bold text-white bg-[#005EB8] hover:bg-[#004a93] rounded-lg px-4 py-2.5 transition-colors min-h-[44px] shrink-0">
                <UserPlus className="h-4 w-4" /> Add staff
              </button>
            </div>
            {addOpen && (
              <form onSubmit={addPerson} className="bg-white rounded-xl border border-slate-200 p-4 mb-6 grid sm:grid-cols-2 gap-3" data-testid="rota-add-staff-form">
                <input required placeholder="Full name *" value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="rota-add-name" className={inputCls} />
                <input type="email" placeholder="Email" value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="rota-add-email" className={inputCls} />
                <input type="tel" placeholder="Mobile number" value={addForm.phone}
                  onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                  data-testid="rota-add-phone" className={inputCls} />
                <select value={addForm.grade} onChange={(e) => setAddForm((f) => ({ ...f, grade: e.target.value }))}
                  data-testid="rota-add-grade" className={inputCls}>
                  {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
                <button type="submit" disabled={saving} data-testid="rota-add-submit"
                  className="sm:col-span-2 h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
                  {saving ? "Adding…" : "Add to staff list"}
                </button>
              </form>
            )}
            {staff.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-testid="rota-empty">
                <UserRound className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-medium">No staff booked yet — book people onto events from the Calendar.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rota-staff-list">
                {staff.map((s, i) => (
                  <div key={s.key} onClick={() => openStaff(s.key)} data-testid={`rota-staff-card-${i}`} role="button" tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && openStaff(s.key)}
                    className="bg-white rounded-xl border border-slate-200 p-5 text-left hover:border-[#005EB8] hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-11 w-11 rounded-full bg-[#0F2547] text-white grid place-items-center font-bold text-sm shrink-0">
                        {s.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-heading font-bold text-[#0F2547] truncate">{s.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{[s.grade_label, s.role_label || s.email].filter(Boolean).join(" · ") || "—"}</p>
                      </div>
                    </div>
                    {!s.accepted && (
                      <p className="mb-2 text-[11px] font-bold text-[#ED8B00] bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid={`rota-new-${i}`}>
                        New sign-up — needs accepting before they can work
                      </p>
                    )}
                    <div className="flex items-center justify-between text-[11px] font-semibold">
                      <span className="text-slate-500">{s.total_events} event{s.total_events === 1 ? "" : "s"}{s.upcoming_count > 0 && ` · ${s.upcoming_count} upcoming`}</span>
                      <span className="text-slate-400">{s.doc_count} doc{s.doc_count === 1 ? "" : "s"}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <span className="text-xs font-bold text-[#D5281B]" data-testid={`rota-owed-${i}`}>Owed {gbp(s.amount_owed)}</span>
                      <span className="text-xs font-bold text-emerald-600" data-testid={`rota-paid-${i}`}>Paid {gbp(s.amount_paid)}</span>
                    </div>
                    {embedded && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        {!s.accepted && (
                          <button onClick={() => acceptStaff(s)} data-testid={`rota-accept-${i}`}
                            className="flex-1 h-9 rounded-lg bg-[#007F3B] hover:bg-[#006331] text-white text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Accept — give access
                          </button>
                        )}
                        <button onClick={() => deleteStaff(s)} data-testid={`rota-delete-${i}`} title="Remove from staff pool" aria-label={`Remove ${s.name}`}
                          className="h-9 px-3 rounded-lg border border-slate-200 text-slate-400 hover:border-[#D5281B] hover:text-[#D5281B] inline-flex items-center justify-center transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div data-testid="rota-staff-detail">
            <button onClick={() => setSelected(null)} data-testid="rota-detail-back"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#005EB8] hover:underline mb-4 min-h-[36px]">
              <ArrowLeft className="h-4 w-4" /> All staff
            </button>

            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="rota-detail-actions">
              <button onClick={emailShifts} disabled={saving} data-testid="rota-email-shifts-btn"
                className="inline-flex items-center gap-2 text-xs font-bold text-white bg-[#005EB8] hover:bg-[#004a93] rounded-lg px-4 py-2.5 transition-colors min-h-[40px] disabled:opacity-60">
                <Mail className="h-3.5 w-3.5" /> Email all their shifts
              </button>
              <button onClick={getFeedLink} disabled={saving} data-testid="rota-feed-link-btn"
                className="inline-flex items-center gap-2 text-xs font-bold text-[#0F2547] border border-slate-300 hover:border-[#005EB8] rounded-lg px-4 py-2.5 transition-colors min-h-[40px] disabled:opacity-60">
                <CalendarDays className="h-3.5 w-3.5" /> Calendar feed link
              </button>
            </div>
            {feedUrl && (
              <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid="rota-feed-panel">
                <p className="text-xs font-semibold text-[#0F2547] mb-1">Calendar subscription — send this to {selected.name.split(" ")[0]} once. Their calendar then updates itself forever (new shifts appear, changed shifts update, never duplicates).</p>
                <p className="text-[11px] text-slate-500 break-all mb-2">{feedUrl}</p>
                <button onClick={async () => { try { await navigator.clipboard.writeText(feedUrl); toast.success("Feed link copied — paste it into a message to them"); } catch { toast.info(feedUrl); } }}
                  data-testid="rota-feed-copy-btn"
                  className="text-xs font-bold text-[#005EB8] hover:underline min-h-[32px]">Copy feed link</button>
                <p className="text-[11px] text-slate-400 mt-1">They add it via Google Calendar → Settings → Add calendar → From URL.</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex flex-col items-center text-center">
                  {photoUrl ? (
                    <img src={photoUrl} alt={selected.name} data-testid="rota-photo"
                      className="h-28 w-28 rounded-full object-cover border-2 border-slate-200 mb-3" />
                  ) : (
                    <div className="h-28 w-28 rounded-full bg-[#0F2547] text-white grid place-items-center font-bold text-2xl mb-3" data-testid="rota-photo-placeholder">
                      {selected.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="rota-photo-input"
                    onChange={(e) => { uploadFile(e.target.files[0], "photo"); e.target.value = ""; }} />
                  <button onClick={() => photoRef.current?.click()} data-testid="rota-photo-upload-btn"
                    className="text-xs font-bold text-[#005EB8] hover:underline inline-flex items-center gap-1.5 min-h-[36px]">
                    <Camera className="h-3.5 w-3.5" /> {photoUrl ? "Change photo" : "Add photo"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-center">
                  <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#D5281B]">Owed</p>
                    <p className="font-vitals font-extrabold text-xl text-[#D5281B]" data-testid="rota-detail-owed">{gbp(selected.amount_owed)}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Paid</p>
                    <p className="font-vitals font-extrabold text-xl text-emerald-700" data-testid="rota-detail-paid">{gbp(selected.amount_paid)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Contact & role</h2>
                <div className="space-y-2">
                  <input className={inputCls} value={profile.name} placeholder="Full name" data-testid="rota-profile-name"
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-3.5 text-slate-300" />
                    <input className={`${inputCls} pl-9`} value={profile.email} placeholder="Email" data-testid="rota-profile-email"
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
                  </div>
                  <div className="relative">
                    <Phone className="h-4 w-4 absolute left-3 top-3.5 text-slate-300" />
                    <input className={`${inputCls} pl-9`} value={profile.phone} placeholder="Phone" data-testid="rota-profile-phone"
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
                  </div>
                  <select value={profile.grade || ""} onChange={(e) => setProfile({ ...profile, grade: e.target.value })}
                    data-testid="rota-profile-grade" className={inputCls}>
                    <option value="">Clinical grade not set</option>
                    {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                  <input className={inputCls} value={profile.role_label} placeholder="Role (e.g. Paramedic, Technician)" data-testid="rota-profile-role"
                    onChange={(e) => setProfile({ ...profile, role_label: e.target.value })} />
                  {selected.has_kit && (
                    <p className="text-xs font-semibold text-slate-500 px-1" data-testid="rota-profile-kit">
                      Own kit: <span className={selected.has_kit === "yes" ? "text-[#007F3B]" : "text-[#D5281B]"}>{selected.has_kit === "yes" ? "Yes" : "No"}</span>
                      <span className="text-slate-400 font-normal"> (their answer when they joined)</span>
                    </p>
                  )}
                  <textarea rows={3} value={profile.notes} placeholder="Notes (visas, DBS expiry, anything)" data-testid="rota-profile-notes"
                    onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <button onClick={saveProfile} disabled={saving} data-testid="rota-profile-save"
                    className="w-full h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
                    {saving ? "Saving…" : "Save profile"}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8]">Availability calendar</h2>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setAvailCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))} data-testid="rota-avail-prev"
                      aria-label="Previous month"
                      className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
                    <span className="text-xs font-bold text-[#0F2547] w-24 text-center" data-testid="rota-avail-month">
                      {availCursor.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                    </span>
                    <button onClick={() => setAvailCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))} data-testid="rota-avail-next"
                      aria-label="Next month"
                      className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">
                  {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <span key={d}>{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-testid="rota-avail-calendar">
                  {Array.from({ length: availMonth.offset }).map((_, i) => <span key={`pad-${i}`} />)}
                  {Array.from({ length: availMonth.count }, (_, i) => {
                    const dateStr = isoDay(new Date(availMonth.y, availMonth.m, i + 1));
                    const st = (selected.availability || {})[dateStr] || "";
                    const past = dateStr < availMonth.today;
                    return (
                      <div key={dateStr} data-testid={`rota-cal-day-${dateStr}`} title={st === "available" ? "Can work" : st === "unavailable" ? "Can't work" : "Not marked"}
                        className={`h-9 rounded-lg text-xs font-bold grid place-items-center ${st === "available" ? "bg-[#007F3B] text-white" : st === "unavailable" ? "bg-[#D5281B] text-white" : "bg-slate-50 text-slate-500"} ${past && !st ? "opacity-30" : ""}`}>
                        {i + 1}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-3 text-[10px] font-bold text-slate-500">
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#007F3B]" /> Can work</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#D5281B]" /> Can't work</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-slate-100 border border-slate-200" /> Not marked</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-2" data-testid="rota-avail-note">Marked by {selected.name.split(" ")[0]} from their own portal (My shifts &amp; pay) — read-only here.</p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">
                  Documents ({selected.documents.length})
                </h2>
                <input ref={docRef} type="file" className="hidden" data-testid="rota-doc-input"
                  onChange={(e) => { uploadFile(e.target.files[0], "documents"); e.target.value = ""; }} />
                <button onClick={() => docRef.current?.click()} data-testid="rota-doc-upload-btn"
                  className="w-full h-11 rounded-lg border border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center justify-center gap-2 mb-3">
                  <Upload className="h-4 w-4" /> Upload document (PDF, image…)
                </button>
                {selected.documents.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">No documents yet — certificates, DBS, contracts…</p>
                ) : (
                  <div className="space-y-2" data-testid="rota-doc-list">
                    {selected.documents.map((d, i) => (
                      <div key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`rota-doc-${i}`}>
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="flex-1 text-xs font-semibold text-[#0F2547] truncate">{d.filename}</span>
                        <button onClick={() => downloadDoc(d)} data-testid={`rota-doc-download-${i}`}
                          className="p-1.5 text-slate-400 hover:text-[#005EB8] transition-colors min-h-[32px]">
                          <Download className="h-4 w-4" />
                        </button>
                        <button onClick={() => deleteDoc(d)} data-testid={`rota-doc-delete-${i}`}
                          className="p-1.5 text-slate-400 hover:text-[#D5281B] transition-colors min-h-[32px]">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3 flex items-center gap-2">
                <Wallet className="h-4 w-4" /> Events & wages
              </h2>
              {selected.entries.length === 0 ? (
                <p className="text-sm text-slate-400">No event bookings yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse min-w-[640px]">
                    <thead>
                      <tr className="bg-[#0F2547] text-white">
                        <th className="text-left px-3 py-2">Event</th>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Shift</th>
                        <th className="text-left px-3 py-2">Hours × rate</th>
                        <th className="text-right px-3 py-2">Amount</th>
                        <th className="text-center px-3 py-2">Paid</th>
                        <th className="text-left px-3 py-2">Date paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.entries.map((e, i) => (
                        <tr key={e.entry_id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`rota-entry-${i}`}>
                          <td className="px-3 py-2.5 font-semibold text-[#0F2547]">{e.event_name}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(e.work_date || e.event_date)}</td>
                          <td className="px-3 py-2.5">{e.time || "—"}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {e.hours != null && e.rate != null ? `${e.hours}h × ${gbp(e.rate)}` : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right font-vitals font-bold">{gbp(e.amount)}</td>
                          <td className="px-3 py-2.5 text-center">
                            <button onClick={() => togglePaid(e)} data-testid={`rota-paid-toggle-${i}`}
                              title={e.paid ? "Paid — click to undo" : "Mark as paid (today)"}
                              className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors ${e.paid ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-transparent hover:border-emerald-500"}`}>
                              <CheckCircle2 className="h-4 w-4" />
                            </button>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <input type="date"
                              value={payDates[e.entry_id] ?? e.paid_date ?? ""}
                              onChange={(ev) => setPayDates((p) => ({ ...p, [e.entry_id]: ev.target.value }))}
                              onBlur={() => savePaidDate(e)}
                              data-testid={`rota-paid-date-${i}`}
                              className="h-9 rounded-lg border border-slate-300 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <input type="number" min="0" step="0.01" placeholder="£ part"
                                value={partAmounts[e.entry_id] ?? ""}
                                onChange={(ev) => setPartAmounts((p) => ({ ...p, [e.entry_id]: ev.target.value }))}
                                data-testid={`rota-part-amount-${i}`} title="Part-payment amount — uses the paid date box for when"
                                className="h-9 w-24 rounded-lg border border-slate-300 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                              <button onClick={() => partPay(e)} data-testid={`rota-part-pay-${i}`}
                                title="Record a part payment (amount + the date in the paid-date box)"
                                className="h-9 px-2.5 rounded-lg border border-[#005EB8] text-[#005EB8] text-[10px] font-bold hover:bg-sky-50 transition-colors">
                                Part pay
                              </button>
                            </div>
                            {(e.part_payments || []).length > 0 && (
                              <div className="mt-1 space-y-0.5" data-testid={`rota-part-list-${i}`}>
                                {e.part_payments.map((pp, j) => (
                                  <p key={j} className="text-[10px] font-semibold text-emerald-700" data-testid={`rota-part-${i}-${j}`}>
                                    {gbp(pp.amount)} on {fmtDate(pp.date)}
                                  </p>
                                ))}
                                <p className="text-[10px] font-bold text-[#D5281B]" data-testid={`rota-part-remaining-${i}`}>
                                  Left to pay {gbp(e.amount - e.part_payments.reduce((a, x) => a + x.amount, 0))}
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
